import JsonApi from 'devour-client';
import config from '@/config';
import store from '@/store';

// Bootstrap
const jsonApi = new JsonApi({
  apiUrl: config.api_endpoint,
  pluralize: false
});

jsonApi.define('customer_tokens', {
  token: '',
  refresh_token: '',
  expires_in: null,
  expires_at: null,
  tenant: '',
  user_id: ''
});

// Define Models
jsonApi.define('credit_cards', {
  initial_token: null,
  exp_month: null,
  exp_year: null,
  brand: null,
  last4: null,
  customer: {
    type: 'customers',
    jsonApi: 'hasOne'
  }
});

jsonApi.define('change_logs', {
  customer_source: null,
  customer_name: null,
  attr_name: null,
  old_value: null,
  new_value: null,
  staff: null,
  created_at: null,
  customer: {
    type: 'customers',
    jsonApi: 'hasOne'
  }
});

jsonApi.define('payments', {
  amount: null,
  status: '',
  error_message: '',
  ext_ref: '',
  payment_reference: '',
  payment_date: null,
  fail_count: null,
  description: '',
  currency: '',
  doc_type: '',
  payment_source: '',
  invoice_id: '',
  omit_accounting_sync: false,
  is_sepa: false,
  customer: {
    type: 'customers',
    jsonApi: 'hasOne'
  },
  invoice: {
    type: 'invoices',
    jsonApi: 'hasOne'
  },
  created_at: null,
  updated_at: null,
  authorization_url: null
});

jsonApi.define('organizations', {
  name: '',
  vat_no: '',
  country_code: '',
  custom_script: '',
  custom_style: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  accounting_adapter: '',
  tos_url: '',
  global_attrs: [],
  is_managed: true,
  logo: '',
  domain_name: '',
  ssl_certificate_frontend: '',
  ssl_certificate_api: '',
  disable_price_quoting: false,
  is_webshipper_supported: false,
  icon: '',
  user_invite_mail_template: {
    jsonApi: 'hasOne',
    type: 'mail_templates'
  },
  user_forgot_password_mail_template: {
    jsonApi: 'hasOne',
    type: 'mail_templates'
  }
});

jsonApi.define('data_processing_agreements', {
  version: '',
  active: false,
  pdf_data: '',
  base64: '',
  requires_customer_approval: false,
  organization: {
    jsonApi: 'hasOne',
    type: 'organizations'
  },
  locale: ''
});

jsonApi.define('archive_customer', {
  id: '',
  archived_at: ''
});

jsonApi.define('customers', {
  name: '',
  tenant_name: '',
  address_1: '',
  address_2: '',
  zip: '',
  city: '',
  ext_ref: '',
  country_code: '',
  time_zone: '',
  plan_id: null,
  state: '',
  payment_method: null,
  vat_no: '',
  ean: '',
  admin_contact_name: '',
  admin_contact_phone: '',
  admin_contact_email: '',
  billing_contact_name: '',
  billing_contact_phone: '',
  billing_contact_email: '',
  currency: null,
  service_status: null,
  vat_percent: null,
  created_at: null,
  updated_at: null,
  api_rate_limit: null,
  api_rate_period_seconds: null,
  default_locale: '',
  login_method: '',
  default_ship_page: '',
  disable_pricequote: 0,
  agreement: '',
  agreed: false,
  onboarding_status: null,
  shipments_last_week: 0,
  overdue_invoices: 0,
  credit_card_count: 0,
  installation_note: '',
  vat_validation_status: '',
  vat_validated_at: '',
  plans: [],
  on_optimal_plan: false,
  predicted_mrr: 0,
  usage_percent: 0,
  predicted_mrr_customer_currency: 0,
  monthly_price: 0,
  crm_reference: null,
  close_lead_id: null,
  third_party_managed: false,
  domains: '',
  subscription: null,
  quote: null,
  customer_reference: null,
  data_processing_agreement_accepted_version: '',
  data_processing_agreement_acceptor_name: '',
  data_processing_agreement_acceptor_phone: '',
  data_processing_agreement_acceptor_email: '',
  archived_at: '',
  require_2fa: false,
  organization: {
    jsonApi: 'hasOne',
    type: 'organizations'
  },
  credit_cards: {
    jsonApi: 'hasMany',
    type: 'credit_cards'
  },
  customer_notes: {
    jsonApi: 'hasMany',
    type: 'customer_notes'
  },
  invoices: {
    jsonApi: 'hasMany',
    type: 'invoices'
  },
  discounts: {
    jsonApi: 'hasMany',
    type: 'discounts'
  },
  taggings: {
    jsonApi: 'hasMany',
    type: 'taggings'
  },
  payment_term: {
    jsonApi: 'hasOne',
    type: 'payment_terms'
  },
  sales_agent: {
    jsonApi: 'hasOne',
    type: 'staffs'
  },
  onboarding_agent: {
    jsonApi: 'hasOne',
    type: 'staffs'
  },
  customer_success_agent: {
    jsonApi: 'hasOne',
    type: 'staffs'
  },
  billed_plan: {
    jsonApi: 'hasOne',
    type: 'plans'
  },
  change_logs: {
    jsonApi: 'hasMany',
    type: 'change_logs'
  },
  replaced_subscriptions: {
    jsonApi: 'hasMany',
    type: 'subscriptions'
  }
});

jsonApi.define('close_com_lead', {
  lead: []
});

jsonApi.define('order_channel_indices', {
  name: '',
  order_channel_type_id: '',
  customer_id: '',
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  order_channel_type: {
    jsonApi: 'hasOne',
    type: 'order_channel_types'
  }
});

jsonApi.define('carrier_indices', {
  name: '',
  carrier_type_id: '',
  carrier_attrs: '',
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  carrier_type: {
    jsonApi: 'hasOne',
    type: 'carrier_types'
  }
});

jsonApi.define('payment_terms', {
  name: '',
  external_reference: '',
  invoicing_type: '',
  days: 0
});

jsonApi.define('discounts', {
  discount_type: '',
  value: 0,
  comment: '',
  status: '',
  discount_cause: '',
  discount_source: '',
  created_at: null,
  updated_at: null,
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  }
});

jsonApi.define('invoices', {
  invoice_lines: [],
  invoice_no: null,
  total_amount: 0,
  sub_total_amount: 0,
  currency: null,
  doc_type: null,
  balance_account: null,
  payment_status: null,
  vat_amount: 0,
  ext_ref: '',
  is_paid: false,
  accounting_date: null,
  base64: null,
  credited_amount: '',
  crediting_status: '',
  due_date: '',
  overdue: false,
  notice_step: '',
  error_message: '',
  crediting_cause: '',
  crediting_source: '',
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  invoice: {
    jsonApi: 'hasOne',
    type: 'invoices'
  },
  created_at: null,
  updated_at: null
});
jsonApi.define('plans', {
  name: null,
  included_shipments: 0,
  included_orders: 0,
  included_order_channels: 0,
  included_users: 0,
  included_printers: 0,
  included_carriers: 0,
  included_return_portals: 0,
  included_automations: 0,
  price_shipments: 0,
  price_orders: 0,
  plan_group: '',
  price_order_channels: 0,
  price_users: 0,
  price_printers: 0,
  price_carriers: 0,
  price_return_portals: 0,
  price_automations: 0,
  price_locked_shipments: 0,
  billing_period: 'monthly',
  currency: 'EUR',
  billing_product_id: null,
  payment_fee_percentage: 1,
  bank_payment_fee: 0,
  created_at: null,
  updated_at: null,
  price: 0,
  visible: false,
  level_one_support_price: 0,
  level_two_support_price: 0,
  level_three_support_price: 0,
  support_class: '',
  status: 'active',
  feature_scopes: '',
  minimum_shipments: 0,
  minimum_orders: 0,
  generation: 1,
  tier: 0,
  category: '',
  include_deleted_orders: false,
  subscriptions: {
    jsonApi: 'hasMany',
    type: 'subscriptions'
  },
  organization: {
    jsonApi: 'hasOne',
    type: 'organizations'
  }
});

jsonApi.define('products', {
  billing_product_id: null,
  product_type: null,
  prices: [],
  descriptions: []
});

jsonApi.define('invoice_previews', {
  customer_id: null,
  invoice: null
});

jsonApi.define('subscriptions', {
  name: null,
  included_shipments: 0,
  included_order_channels: 0,
  included_users: 0,
  included_printers: 0,
  included_carriers: 0,
  included_return_portals: 0,
  included_automations: 0,
  included_orders: null,
  price_shipments: 0,
  price_orders: 0,
  price_order_channels: 0,
  price_users: 0,
  price_printers: 0,
  price_carriers: 0,
  price_return_portals: 0,
  price_automations: 0,
  price_staffs: 0,
  price_customers: 0,
  setup_cost: 0,
  billing_period: 'monthly',
  billing_period_start: null,
  billing_period_end: null,
  usage_period: 'monthly',
  usage_period_start: null,
  usage_period_end: null,
  cancellation_date: null,
  cancellation_periods: 1,
  currency: 'EUR',
  billing_product_id: null,
  payment_fee_percentage: 1,
  bank_payment_fee: 0,
  created_at: null,
  updated_at: null,
  switch_to_plan_id: null,
  subscription_discount: null,
  price: 0,
  status: 'active',
  minimum_shipments: 0,
  support_fee: 0,
  subscription_type: 'customer',
  feature_scopes: '',
  level_one_support_price: 0,
  level_two_support_price: 0,
  level_three_support_price: 0,
  support_class: '',
  go_live_date: null,
  viewed_at: null,
  quote_link: '',
  activation_time: null,
  cancellation_cause: '',
  cancellation_source: '',
  cancellation_comment: '',
  cancellation_creation_date: null,
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  plan: {
    jsonApi: 'hasOne',
    type: 'plans'
  },
  white_label_organization: {
    jsonApi: 'hasOne',
    type: 'organizations'
  },
  quote_lines: {
    jsonApi: 'hasMany',
    type: 'quote_lines'
  }
});

jsonApi.define('quote_lines', {
  model_type: null,
  model_id: null,
  description: null,
  quantity: 0,
  unit_price: 0.0,
  subscription: {
    jsonApi: 'hasOne',
    type: 'subscriptions'
  },
  product: {
    jsonApi: 'hasOne',
    type: 'products'
  }
});

jsonApi.define('barcode_ranges', {
  serial_from: 0,
  serial_to: 0,
  barcode_type: '',
  current_serial: 0
});

jsonApi.define('customer_notes', {
  note: '',
  staff_id: null,
  created_at: null,
  updated_at: null,
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  staff: {
    jsonApi: 'hasOne',
    type: 'staffs'
  }
});

jsonApi.define('user_groups', {
  name: '',
  scopes: '',
  visible: false,
  created_at: null,
  updated_at: null,
  locked_user_template: false
});

jsonApi.define('staffs', {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  slack_id: '',
  password: '',
  customer_notes: {
    jsonApi: 'hasMany',
    type: 'customer_notes'
  },
  user_group: {
    jsonApi: 'hasOne',
    type: 'user_groups'
  },
  organization: {
    jsonApi: 'hasOne',
    type: 'organizations'
  },
  created_at: null,
  updated_at: null
});

jsonApi.define('order_channel_types', {
  name: '',
  component_name: '',
  fulfillment_job_name: '',
  support_url: '',
  list_logo: null,
  description: '',
  local_attrs: [],
  global_attrs: [],
  module_link: '',
  can_autofulfill: true,
  can_limit_drop_points: false,
  supports_rate_quoting: false,
  uses_scheduled_import: true,
  supports_time_interval_import: false,
  supports_statuses_import: false,
  supports_id_import: false,
  supports_vat_in_checkout: false,
  onboarding_url: '',
  create_configuration_token: false,
  hide: false
});

jsonApi.define('purchases', {
  unit_price: '',
  description: '',
  currency: '',
  created_at: null,
  is_billed: false,
  quantity: '',
  plan: {
    jsonApi: 'hasOne',
    type: 'plans'
  },
  product: {
    jsonApi: 'hasOne',
    type: 'products'
  },
  invoice: {
    jsonApi: 'hasOne',
    type: 'invoices'
  },
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  }
});



jsonApi.define('carrier_types', {
  name: '',
  carrier_code: '',
  carrier_group_id: '',
  booking_endpoint_url: '',
  prebooking_endpoint_url: '',
  validation_endpoint_url: '',
  carrier_request_endpoint_url: '',
  carrier_response_endpoint_url: '',
  is_public: false,
  supports_zpl: false,
  service_list_url: '',
  service_quote_url: '',
  waybill_endpoint_url: '',
  pickup_url: '',
  eod_url: '',
  drop_point_url: '',
  drop_point_support: false,
  fulfillment_logo: null,
  list_logo: null,
  fulfillment_logo_base64: null,
  list_logo_base64: null,
  description: '',
  required_details: '',
  specification_version: 'v1',
  local_attrs: [],
  global_attrs: [],
  address_validation_validators: [],
  address_validation_filters: [],
  requires_dutiable: null,
  requires_approval: false,
  supports_price_quoting: false,
  rate_quote_validation: false,
  disable_prequote: false,
  supports_tracking: false,
  supports_edi_parsing: false,
  tracking_url: '',
  tracking_edi_url: '',
  exclude_billing: false,
  check_rotation: false,
  supports_documents: false,
  supports_shipment_updates: false,
  shipment_updates_limit_minutes: '0',
  barcode_mail: '',
  export_document_email_to: '',
  customer_specific_services: false,
  receive_custom_customs_document: false,
  supports_customs: false,
  price_parser_class: '',
  cost_lines: [],
  shipment_deletion_endpoint_url: '',
  onboarding_url: '',
  colli_type_support: null,
  beta: false,
  supports_test_mode: false,
  specific_volume_parameter: '',
  convert_to_currency: '',
  show_send_time: false,
  supports_shadow_bookings: false,
  supports_pickup: false,
  service_translations: '',
  freightsolution_service_mapping: {},
  freightsolution_name: '',
  freightsolution_services: [],
  services: [],
  require_ftp_configuration_id: false,
  transmit_shipments: false,
  hide: false,
  carrier_library_validation: false,
  is_edi: false,
  excluded_subcarriers: [],
  order_barcode_mail_template: {
    jsonApi: 'hasOne',
    type: 'mail_templates'
  },
  barcode_customer_notification_mail_template: {
    jsonApi: 'hasOne',
    type: 'mail_templates'
  },
  export_document_email_template: {
    jsonApi: 'hasOne',
    type: 'mail_templates'
  }
});

jsonApi.define('local_attrs', {
  attr_name: '',
  attr_key: '',
  attr_type: '',
  attr_value: '',
  category: '',
  priority: '',
  local_attr_translations: {
    jsonApi: 'hasMany',
    type: 'local_attr_translations'
  },
  order_channel_type: {
    jsonApi: 'hasOne',
    type: 'order_channel_types'
  }
});

jsonApi.define('local_attr_translations', {
  attr_key: '',
  locale: '',
  translation: ''
});

jsonApi.define('address_validation_filters', {
  validator: '',
  condition: '',
  carrier_type: {
    jsonApi: 'hasOne',
    type: 'carrier_types'
  }
});

jsonApi.define('address_validation_validators', {
  validator: '',
  condition: '',
  params: '',
  carrier_type: {
    jsonApi: 'hasOne',
    type: 'carrier_types'
  }
});

jsonApi.define('oauth_applications', {
  name: '',
  scopes: '',
  redirect_uri: '',
  uid: '',
  secret: ''
});

jsonApi.define('automations', {
  trigger: {},
  actions: []
});

jsonApi.define('tags', {
  name: '',
  customers: {
    jsonApi: 'hasMany',
    type: 'customers'
  }
});

jsonApi.define('taggings', {
  name: '',
  tag: {
    jsonApi: 'hasOne',
    type: 'tags'
  },
  taggable: {
    jsonApi: 'hasOne'
  }
});

jsonApi.define('mail_template_previews', {
  mail_template: null,
  mail: null,
  mail_locale: null
});

jsonApi.define('mail_templates', {
  name: '',
  default_locale: 'en',
  mail_locales: [],
  images: []
});

/**
 * @typedef {object} jsonApiResourceMailBuilderGlobalTemplateExtra
 * @property {jsonApiResourceMailBuilderGlobalTemplate} mail_builder_global_template
 */

/**
 * @typedef {object} jsonApiResourceMailBuilderGlobalTemplate
 * @property {number} [id]
 * @property {string} name
 * @property {string} hook
 * @property {string[]} models
 * @property {string[]} active_locales
 * @property {string} description
 * @property {jsonApiResourceMailBuilderGlobalAttribute[]} attribute_types
 * @return {jsonApiResourceMailBuilderGlobalTemplate}
 */
// function jsonApiResourceMailBuilderGlobalTemplate() {}
jsonApi.define('mail_builder_template_types', {
  name: '',
  hook: '',
  models: '',
  active_locales: [],
  description: '',
  thumbnail: '',
  attribute_types: []
});

/**
 * @typedef {object} jsonApiResourceMailBuilderGlobalAttribute
 * @property {number} [id]
 * @property {string} key
 * @property {string} value
 * @property {('string'|'text'|'boolean'|'image'|'color')} input_type
 * @property {string} avaliable_liquids CSV ....
 * @property {boolean} required
 * @property {boolean} visible
 * @property {jsonApiResourceMailBuilderGlobalTemplate} global_template
 * @property {jsonApiResourceMailBuilderGlobalLocale[]} global_locales
 */
jsonApi.define('mail_builder_attribute_types', {
  key: '',
  value: '',
  input_type: '',
  avaliable_liquids: '',
  required: '',
  visible: '',
  template_type: {
    jsonApi: 'hasOne',
    type: 'mail_builder_template_types'
  },
  locale_types: []
});

/**
 * @typedef {object} jsonApiResourceMailBuilderGlobalLocale
 * @property {string} value
 * @property {string} locale
 * @property {jsonApiResourceMailBuilderGlobalAttribute} attribute_type
 */
jsonApi.define('mail_builder_locale_types', {
  value: '',
  locale: '',
  attribute_type: {
    jsonApi: 'hasOne',
    type: 'mail_builder_attribute_types'
  }
});

jsonApi.define('error_messages', {
  matcher: '',
  carrier_service_code: '',
  translations: {},
  error_class: '',
  support_url: '',
  count: 0,
  weight: 0,
  created_at: '',
  carrier_type: {
    jsonApi: 'hasOne',
    type: 'carrier_types'
  }
});

jsonApi.define('error_class_messages', {
  translations: {},
  countries: '',
  error_class: '',
  support_url: '',
  created_at: '',
  carrier_type: {
    jsonApi: 'hasOne',
    type: 'carrier_types'
  }
});

jsonApi.define('error_order_channel_messages', {
  translations: {},
  matcher: '',
  error_class: '',
  support_url: '',
  count: 0,
  weight: 0,
  created_at: '',
  order_channel_type: {
    jsonApi: 'hasOne',
    type: 'order_channel_types'
  }
});

jsonApi.define('error_fulfillment_messages', {
  translations: {},
  matcher: '',
  error_class: '',
  count: 0,
  created_at: '',
  order_channel_type: {
    jsonApi: 'hasOne',
    type: 'order_channel_types'
  }
});

jsonApi.define('address_validation_zip_formats', {
  country_code: '',
  formats: ''
});

jsonApi.define('account_lines', {});

jsonApi.define('billing_events', {
  body: '',
  created_at: '',
  record: {
    jsonApi: 'hasOne'
  },
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  }
});

jsonApi.define('capabilities', {
  carrier_code: '',
  customer_specific_attributes: {},
  services: []
});

jsonApi.define('staff_accesses', {
  accepted_by_customer: false,
  accepted_by_staff: false,
  start_time: '',
  end_time: '',
  description: '',
  plan_id: '',
  price_per_hour: '',
  hours_estimated: '',
  hours_spent: '',
  completed: false,
  action_log_path: '',
  start_staff_access: false,
  created_at: '',
  staff: {
    jsonApi: 'hasOne',
    type: 'staffs'
  },
  customer: {
    jsonApi: 'hasOne',
    type: 'customers'
  },
  purchase: {}
});

jsonApi.define('white_label_reports', {
  id: '',
  period_start: '',
  period_end: '',
  status: '',
  download_url: '',
  created_at: ''
});

jsonApi.define('carrier_library_setups', {
  carrier_concept_id: '',
  fulfillment_logo: '',
  name: '',
  list_logo: '',
  carrier_code: '',
  specification_version: '',
  global_attrs: [],
  local_attrs: [],
  supports_zpl: false,
  colli_type_support: '',
  disable_prequote: false
});

jsonApi.define('external_order_channel_type_configs', {
  types: []
});

jsonApi.define('revoke_user_2fa_tokens_for_customer', {
  tenant: '',
  email: ''
});

jsonApi.logger = console;

jsonApi.insertMiddlewareAfter('axios-request', {
  name: '401-detector',
  error: function (payload) {
    if (payload.response.status === 401) {
      console.log('Logged out');
      store.commit('setToken', null);
      store.commit('setRefreshToken', null);
    }

    console.log('Payload error', payload);
    return Promise.reject(payload.response.data);
  }
});

jsonApi.findAllPages = async function (modelName, params) {
  let data = [];
  let page = 1;
  let links = {};
  let meta = {};
  let errors = [];

  do {
    let {
      data: d,
      links: l,
      errors: e,
      meta: m
    } = await this.findAll(modelName, { ...params, page: { number: page } });
    page++;
    links = l;
    errors = errors.concat(e);
    meta = { ...meta, ...m };
    data = data.concat(d);
  } while (links.next);

  return { data: data, meta: meta, links: links, errors: errors };
};

window.jsonApi = jsonApi;
export default jsonApi;
